// University of Rhode Island: public college page (#2840), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - URI's Common Data Set 2025-2026 (CDS-PDF-2025-2026_fillablePDF.pdf,
//    linked from Institutional Research's Common Data Set page): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 plans. Quirks: C21 gives
//    202 early decision applicants and 150 admits but labels them "For the
//    Fall 2024 entering class", so no early rate is shown; and the G section
//    is marked as not yet final ("available 7/1/2026") with tuition that
//    matches URI's 2025-26 figures, so the cost table uses the financial aid
//    office's 2026-27 cost of attendance instead.
//  - Undergraduate Admission: First-Year Student Admission (deadlines,
//    major-specific requirements, testing) and Early Decision and Early
//    Action (binding terms). Neither page names the entering year; the
//    deadlines are the ones posted for the application open on 2026-10-01,
//    and the CDS confirms the test policy for Fall 2027.
//  - Financial Aid, 2026-27 Estimated Undergraduate Cost of Attendance, on
//    campus, Rhode Island residents and out-of-state students.
//  - College Scorecard, school id 217484 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'URI Common Data Set 2025-2026',
        url: 'https://web.uri.edu/ir/data/common/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'URI Undergraduate Admission, First-Year Student Admission',
        url: 'https://web.uri.edu/admission/first-year/',
        read: '2026-10-01',
    },
    early: {
        label: 'URI Undergraduate Admission, Early Decision and Early Action',
        url: 'https://web.uri.edu/admission/early-decision-and-early-action/',
        read: '2026-10-01',
    },
    coa: {
        label: 'URI Financial Aid, 2026-27 Estimated Undergraduate Cost of Attendance',
        url: 'https://web.uri.edu/financial-aid/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?217484-University-of-Rhode-Island',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-rhode-island',
    kbId: 'university_of_rhode_island',
    name: 'University of Rhode Island',
    shortName: 'URI',
    location: 'Kingston, RI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (web.uri.edu/brand); decoration only.
    branding: {
        primary: '#002147',
        secondary: '#D0A627',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_rhode_island_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 28021,
        admitted: 20892,
        enrolled: 3431,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4368, admitted: 3179 },
            { group: 'Out-of-state', applied: 23100, admitted: 17302 },
            { group: 'International', applied: 553, admitted: 411 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'URI\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'URI\'s Common Data Set lists 202 early decision applicants and 150 admitted, but labels them for the Fall 2024 entering class rather than Fall 2025, so no early rate is shown here. It gives no early action counts; early action is not restrictive.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to URI with an intended major. Some majors ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'A required Nursing statement, and URI strongly encourages applying by the December 1 early action deadline. Applicants should show strength in math and science, including biology and chemistry. If you are not admissible to Nursing, you may be able to ask to be considered for another major, as space allows.' },
            { school: 'Pharm.D.', requirement: 'Admission to the zero-to-six Doctor of Pharmacy program is competitive. It asks for a supplemental essay and two letters of recommendation, and encourages four years of math (through precalculus or calculus), chemistry and biology. If you are not admitted directly, you may be eligible to pursue the Pharm.D. through a B.S.P.S. pathway.' },
            { school: 'Engineering', requirement: 'URI encourages four units of math (including precalculus or calculus) and three of science with at least one lab, physics and chemistry strongly encouraged. If you are not admitted directly, you may be admitted to a pre-engineering pathway.' },
            { school: 'Music', requirement: 'Every music major, music therapy included, requires an audition. If you are admitted to URI before you audition, your place in the major depends on a successful audition. Music therapy applicants are strongly encouraged to apply by December 1.' },
            { school: 'Honors and Talent Development', requirement: 'Both ask for a supplemental essay. The Talent Development Program deadline is December 15, with decisions in February.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Action', deadline: 'December 1', decision: 'By January 31', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By March 31', binding: false },
        ],
        note: 'December 1 is also the priority deadline for merit scholarships. The application fee is $65, or a fee waiver. Early decision deposits are due February 1, or within two weeks of your financial aid award if you applied for aid; for everyone else, May 1.',
        source: 'firstYear',
        binding: 'Early Decision is binding: you and a parent or guardian sign an agreement, and if admitted you enroll at URI and withdraw your other applications. You are released if you are not admitted to your first-choice major, or if URI asks for your senior-year grades; your application then moves to Regular Decision, with a decision by late March. Early Action is non-binding. Early decision is not an option for Rhode Island students applying to the Talent Development Program.',
        bindingSource: 'early',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'URI is test-optional. If you choose to send SAT or ACT scores, they must come electronically from the testing service, and URI superscores: it takes the highest combination of section scores across all test dates. The ACT writing section and the optional SAT essay are not required.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15684, 37350] },
            { item: 'Required fees', amounts: [2396, 2396] },
            { item: 'Food and housing (on campus)', amounts: [16704, 16704] },
            { item: 'Books and supplies', amounts: [1330, 1330] },
            { item: 'Transportation', amounts: [320, 1201] },
            { item: 'Loan origination fee', amounts: [72, 72] },
            { item: 'Personal expenses', amounts: [2430, 2430] },
        ],
        totals: [38936, 61483],
        note: 'For a full-time undergraduate living on campus, fall and spring. The same page lists a New England Regional rate with $27,448 in tuition. Some programs add fees, such as $1,500 a year in engineering, and health insurance is billed unless you waive it.',
        source: 'coa',
        netPrice: {
            average: 22469,
            byIncome: [
                { income: '$0 to $30,000', amount: 15297 },
                { income: '$30,001 to $48,000', amount: 15468 },
                { income: '$48,001 to $75,000', amount: 17805 },
                { income: '$75,001 to $110,000', amount: 23068 },
                { income: 'Over $110,000', amount: 31501 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from URI\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
